import { TableV3ColDefs, TableV3FilterItem } from 'common/table/types/TableV3';
import { ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { TableHeader } from 'common/table/components/TableHeader';
import { getSortedColumn } from 'common/table/utils/getSortedColumn';
import { RouterLink } from 'common/ui/RouterLink';
import { calculateColumnMinWidth } from 'common/table/utils/calculateColumnMinWidth';

export type MapColumnsOnSearchProps<T> = {
  column: keyof T;
  value: string;
};

export type MapColumnsOnFilterSelectProps<T> = {
  column: keyof T;
  item: TableV3FilterItem;
};

export type MapColumnsOnFilterApplyProps<T> = {
  column: keyof T;
  items: TableV3FilterItem[];
};

type MapColumnsProps<T, K> = {
  columns: TableV3ColDefs<T, K>;
  onSearch: ({ column, value }: MapColumnsOnSearchProps<T>) => void;
  onFilterSelect: ({ column, item }: MapColumnsOnFilterSelectProps<T>) => void;
  onFilterApply: ({ column, items }: MapColumnsOnFilterApplyProps<T>) => void;
};

export const mapColumns = <T, K>({
  columns,
  onSearch,
  onFilterSelect,
  onFilterApply
}: MapColumnsProps<T, K>): TableV3ColDefs<T, K> => {
  const mappedColumns = columns.map((column) => {
    const field = column.field as unknown as keyof T;
    column.comparator = () => 0;
    column.maxWidth = 500;

    // Calculate minWidth based on header name and icons
    const headerName = column.headerName || (column.field as string);
    const hasFilterOptions =
      column.filterOptions !== undefined ||
      column.serverFilterOptions !== undefined ||
      column.rangeFilter !== undefined;
    const hasMenuOptions =
      column.searchable || column.sortField !== undefined || hasFilterOptions;

    column.minWidth =
      column.minWidth ||
      calculateColumnMinWidth(headerName, {
        hasHeaderIcon: column.headerIcon !== undefined,
        isSearchable: column.searchable || false,
        hasFilterOptions,
        isSortable: column.sortField !== undefined,
        hasMenuOptions
      });

    if (column.sortField) {
      column.cellClassRules = {
        'ag-grid-sorted-column': (params) => {
          // Check if cell is selected
          const isSelected = params.node.isSelected();
          if (isSelected) {
            return false;
          }
          const columnState = params.api.getColumnState();
          const sortedColumn = getSortedColumn<K>(columnState);
          return sortedColumn?.field === field;
        }
      };
    }

    column.headerComponent = (headerColumn: IHeaderParams) => {
      const columnState = headerColumn.api.getColumnState();
      const sortedColumn = getSortedColumn<K>(columnState);
      const isSorted = sortedColumn?.field === field;

      // Access filterValues from ag-grid context
      const filterValues = headerColumn.context?.filterValues || {};
      const filterItems = headerColumn.context?.filterItems || {};

      return (
        <TableHeader
          displayName={headerColumn.displayName}
          isSorted={isSorted}
          isSearchable={column.searchable || false}
          searchPlaceholder={column.searchPlaceholder}
          isSortable={column.sortField !== undefined}
          filterOptions={
            column.filterOptions
              ? {
                  items: column.filterOptions.items,
                  selectedValues: filterValues[field] || [],
                  selectedItems: filterItems[field] || [],
                  selectionType: column.filterOptions.selectionType,
                  isSearchable: column.filterOptions.isSearchable,
                  headerContent: column.filterOptions.headerContent,
                  showCheckboxes: column.filterOptions.showCheckboxes,
                  requiresApply: column.filterOptions.requiresApply
                }
              : undefined
          }
          serverFilterOptions={
            column.serverFilterOptions
              ? {
                  selectedValues: filterValues[field] || [],
                  selectedItems: filterItems[field] || [],
                  selectionType: column.serverFilterOptions.selectionType,
                  onSearchFilter: column.serverFilterOptions.onSearchFilter,
                  headerContent: column.serverFilterOptions.headerContent,
                  showCheckboxes: column.serverFilterOptions.showCheckboxes,
                  searchPlaceholder:
                    column.serverFilterOptions.searchPlaceholder,
                  requiresApply: column.serverFilterOptions.requiresApply
                }
              : undefined
          }
          rangeFilter={
            column.rangeFilter
              ? {
                  ...column.rangeFilter,
                  selectedItems: filterItems[field] || []
                }
              : undefined
          }
          sortDirection={sortedColumn?.direction || 'asc'}
          onSort={(direction) => {
            headerColumn.setSort(direction);
          }}
          onSearch={(value) => {
            onSearch({ column: field, value });
          }}
          onFilterSelect={(item: TableV3FilterItem) => {
            onFilterSelect({ column: field, item });
          }}
          onFilterApply={(items: TableV3FilterItem[]) => {
            onFilterApply({ column: field, items });
          }}
          headerIcon={column.headerIcon}
        />
      );
    };

    const renderLink = column.renderLink;

    if (renderLink) {
      column.cellRenderer = (params: ICellRendererParams<T>) => {
        if (!params.data) {
          return '';
        }
        const link = renderLink(params.data);
        return <RouterLink to={link}>{params.value}</RouterLink>;
      };
    }

    return column;
  });

  return mappedColumns;
};
